import { Package } from 'lucide-react';

/**
 * SPEC-GAP: §6 lists Button, Chip, Card, FactRow, ParticipantChip, ComplianceNotice,
 * AnnouncementCard, MessageBubble and EmptyState — no item badge. Per §10 ("prefer the nearest
 * specified pattern") this is built from existing tokens only, and the pattern it copies is
 * `EventPacking`'s read-only assignee chip: `t-caption inline-flex items-center gap-1`, a lucide
 * icon at 13px, `aria-hidden` on the glyph. No new colour, size or radius.
 *
 * ---
 *
 * A line on a packing list that is the troop's, and comes back — TROOP-167/169.
 *
 * **One component and six call sites**, because a packing item row is hand-rolled in six places —
 * `EventPacking`'s SharedList, AssignedRow and MyKit rows, `FamilyPacking`'s two, and
 * `PackingListsScreen`'s template card. Adding a badge six times is six chances for one of them to
 * say something slightly different, which is the drift `ParticipantStrip` and `PeopleGrid` exist to
 * prevent.
 *
 * It is deliberately *not* a whole row component. The six rows genuinely differ in what else they
 * carry — an assignee select, a remove button, a checkbox, nothing — and the thing that must not
 * drift is the **label and the icon**, not the layout around them. That is the honest boundary.
 *
 * **Never colour alone.** The word carries the meaning and the icon only accompanies it, so
 * stripping the hue changes nothing — the same reasoning §8 gives for the event palette and
 * `ParticipantChip` gives for youth/adult shape.
 *
 * **The wording says what it means to the reader rather than naming the flag**: *The troop's*, not
 * *UnitOwned*. This is content rather than chrome (rule 16), which is what makes the literal
 * acceptable — the same category as an event a troop names "Scout Sunday". If it ever needed to
 * name the *kind* of unit it would render the unit's own word from data instead.
 *
 * **A guardian sees this on `/family` exactly as a member does on the event page.** Nothing about
 * it is audience-shaped: a parent should know the troop's sleeping bag is in the boot of their car,
 * and this is how they find out.
 *
 * **No `paper` prop and no print twin.** `.pg-print-only`'s base `display: none` lives only inside
 * `EventRosterScreen`'s `PrintStyles`, so a twin rendered here would simply show as a stray glyph
 * beside every control on the event page. The gear sheets are their own screen with their own
 * styles.
 */
export function ItemBadge() {
  return (
    <span
      className="t-caption inline-flex items-center gap-1"
      style={{ color: 'var(--color-text-secondary)' }}
    >
      <Package size={13} aria-hidden="true" className="shrink-0" />
      The troop&rsquo;s
    </span>
  );
}

/**
 * The same thing, as a control — TROOP-168's officer act, which had no screen until now.
 *
 * **A checkbox, not a tap on the badge**, and TROOP-135 is the reason: this cell has to hold both
 * the state and the way to change it, and an icon that is also a button reads as whichever of the
 * two the viewer guesses. A box is unambiguous in both directions — checked is *the troop's*,
 * unchecked is *the family's*, and the same control undoes it.
 *
 * **Rendered only for somebody who may actually set it**, so it is an absence rather than a
 * disabled control (the `Button` rule, applied where there is nothing useful to say to a member
 * about why). Everybody else gets {@link ItemBadge}, which says the same thing and cannot be
 * changed — the read half is not officer-only, because a family needs to know the troop's sleeping
 * bag is in their car.
 *
 * **It is legal on a template**, which is the point: mark the trailer once on the unit's standard
 * gear list and every trip published from it inherits the flag.
 */
export function UnitOwnedToggle({
  itemText,
  isUnitOwned,
  busy,
  onChange,
}: {
  itemText: string;
  isUnitOwned: boolean;
  busy: boolean;
  onChange: (isUnitOwned: boolean) => void;
}) {
  return (
    <label
      className="t-caption inline-flex items-center gap-1"
      style={{
        color: 'var(--color-text-secondary)',
        cursor: busy ? 'default' : 'pointer',
        // Constant width, so a column of these lines up. The row lets the item name absorb its
        // free space; this is the other half of that -- a control that sized to its content would
        // still wander, and the whole point of the pair is a straight edge to read down.
        flex: '0 0 auto',
        whiteSpace: 'nowrap',
      }}
    >
      <input
        type="checkbox"
        checked={isUnitOwned}
        disabled={busy}
        onChange={(e) => onChange(e.target.checked)}
        /*
          The accessible name names the item, because a column of identical "The troop's" labels
          tells a screen reader nothing about which line it is on.

          An `aria-label` rather than a visually-hidden span, and that is a layout decision as much
          as an accessibility one: this codebase's only hiding helper is `.sr-only-focusable`, which
          by design REVEALS itself on `:focus-within` -- so a span inside this label would expand
          the control the moment somebody tabbed onto the box, jogging the row sideways. Nothing to
          reveal is the fix.
        */
        aria-label={`${itemText} is the troop's`}
        style={{ width: 16, height: 16 }}
      />
      <Package size={13} aria-hidden="true" className="shrink-0" />
      {/* aria-hidden, so the visible words do not repeat the name the input already carries. */}
      <span aria-hidden="true">The troop&rsquo;s</span>
    </label>
  );
}
